//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  BatchCreateDraftPostsResponse,
  BatchUpsertDraftPostsResponse,
} from '@bvengineering/universal-scheduling-gateway-service-types';

import { TScheduledPostConfig } from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import {
  ConnectionId,
  TConnectionConfig,
} from '@apis/universal-posts/connections/universalPostConnectionModel';
import {
  ICreateDraftPost,
  IDraftPost,
  IDraftPostResponse,
  IGetDraftPostsResponse,
  ISearchDraftPostsRequest,
  ISearchDraftPostsResponse,
  IUpsertDraftPost,
} from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import { deserializeDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiUtils';
import DraftId from '@models/DraftId';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const DRAFT_POST_DEFAULT_ENDPOINT = '/api/v1/drafts';

const universalDraftPostFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'universal_scheduling_gateway',
    `${DRAFT_POST_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );
};

/**
 * Gets draft posts for the current client
 *
 * GET /api/v1/drafts
 */
export const searchDraftPosts = async (
  req: ISearchDraftPostsRequest,
): Promise<ISearchDraftPostsResponse> => {
  const params = makeParams(req);
  const res = await universalDraftPostFetch(`?${params}`);

  const draftPostsResponse = await res.json<ISearchDraftPostsResponse>(
    (json) => ({
      data: json.data.map(deserializeDraftPost),
      totalCount: json.totalCount,
    }),
  );

  return draftPostsResponse;
};

/**
 * Gets draft posts by id for the current client
 *
 * GET /api/v1/drafts/batch
 */
export const getDraftPosts = async (ids: DraftId[]): Promise<IDraftPost[]> => {
  if (ids.length === 0) {
    return [];
  }

  const params = makeParams({ ids });
  const res = await universalDraftPostFetch(`/batch?${params}`);

  const postResponse = await res.json<IGetDraftPostsResponse>((data) => ({
    data: data.data.map(deserializeDraftPost),
  }));

  return postResponse.data;
};

/**
 * Creates draft posts
 *
 * POST /api/v1/drafts/batch
 */
export const createDraftPosts = async (
  drafts: ICreateDraftPost[],
): Promise<DraftId[]> => {
  const res = await universalDraftPostFetch(`/batch`, {
    method: 'POST',
    body: JSON.stringify({
      drafts,
    }),
  });

  const createdPostsResponse = await res.json<BatchCreateDraftPostsResponse>();

  return createdPostsResponse.data;
};

/**
 * Upserts draft posts
 *
 * POST /api/v1/drafts/batch/upsert
 */
export const upsertDraftPosts = async (
  draftPosts: IUpsertDraftPost[],
  connectionId?: ConnectionId,
  connectionConfiguration?: TConnectionConfig,
): Promise<DraftId[]> => {
  const res = await universalDraftPostFetch(`/batch/upsert`, {
    method: 'POST',
    body: JSON.stringify({
      draftPosts,
      connectionId,
      connectionConfiguration,
    }),
  });

  const draftIdsResponse = await res.json<BatchUpsertDraftPostsResponse>();

  return draftIdsResponse.data;
};

/**
 * Updates a draft post
 *
 * PATCH /api/v1/drafts/:id
 */
export const updateDraftPost = async (
  id: DraftId,
  config: TScheduledPostConfig,
  scheduledTime?: Date,
): Promise<IDraftPost> => {
  const res = await universalDraftPostFetch(`/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      scheduledTime,
      config,
    }),
  });

  const postResponse = await res.json<IDraftPostResponse>((data) => ({
    data: deserializeDraftPost(data.data),
  }));

  return postResponse.data;
};

/**
 * Deletes draft posts by id
 *
 * DELETE /api/v1/drafts/batch/
 */
export const deleteDraftPosts = async (ids: DraftId[]): Promise<void> => {
  const params = makeParams({ ids });

  await universalDraftPostFetch(`/batch?${params}`, {
    method: 'DELETE',
  });
};
